/**
 * Component: ShiftDaySelection
 * @description Provides a list of buttons for selecting a day in the current week. Disables buttons for past dates.
 *
 * @props
 *   @param {function} onSelect - Callback function triggered when a day is selected.
 *   @param {Array} currentWeek - Array of dates representing the current week.
 *
 * @methods
 *   @function onSelect - Called with the selected date when a button is clicked.
 *
 * @render
 *   - Displays a button for each day of the current week with the formatted day name and date.
 *   - Disables buttons for days in the past.
 *
 * @example
 * <ShiftDaySelection
 *   onSelect={(date) => console.log("Selected date:", date)}
 *   currentWeek={[...weekDates]}
 * />
 */

import { formatLocalDate } from '../../../utils/dateUtils';
import { Box, Button, Typography } from '@mui/material';

const ShiftDaySelection = ({ onSelect, currentWeek }) => {
  const today = new Date();

  return (
    <Box p={2}>
      <Typography variant="h6">Select a day this week</Typography>
      <Box display="flex" flexDirection="column" mt={2}>
        {currentWeek.map((day, index) => {
          const date = new Date(day);
          date.setHours(0, 0, 0, 0);

          const formattedDate = formatLocalDate(date, {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
          });

          // Disable button for past dates
          const isPast = date < today.setHours(0, 0, 0, 0);

          return (
            <Button
              key={index}
              onClick={() => onSelect(date)}
              variant="contained"
              disabled={isPast}
              sx={{ marginBottom: '8px', textAlign: 'left' }}
            >
              {formattedDate}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

export default ShiftDaySelection;
